import React, { useState, useEffect } from 'react';
import { Field, Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  TextInput,
  InputMaskField,
  Datepicker,
  CollapseWithLabel,
  Toggler,
  Radio,
} from '@components';
import {
  BeneficiarioTaesaSchema,
  BeneficiarioTaesaInitialValues,
  BeneficiarioTaesaDocs,
} from '@schemas';
import {
  criaFormRascunhoRequest,
  concluirFormRequest,
  excluiItemRascunhoRequest,
  atualizaItemRascunhoRequest,
  adicionaItemRascunhoRequest,
} from '@store/user/actions';
import { useNavigate } from 'react-router-dom';
import { ModalUpload } from './components/ModalUpload';
import { validaUpload } from './utils/validaUpload';

const Anexos = ({ disabled, reset, cpf }) => {
  const beneficiarios = useSelector((state) => state.user.formularioLista);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {BeneficiarioTaesaDocs.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                const itemAtual = beneficiarios.find((dp) => dp.cpf === cpf);
                const anexoAtual = itemAtual?.anexos.find(
                  (anx) => anx.name === anexo.name
                );

                return (
                  <TextInput
                    key={reset ? 'reset' : ''}
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    multiple
                    label={anexo.label}
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                    disabled={disabled}
                    defaultFile={anexoAtual}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

const CardBeneficiario = ({
  nomeBeneficiario,
  cpf,
  nascimento,
  nomeMaeBeneficiario,
  nomePaiBeneficiario,
  anexos,
  acao,

  setDisabled,
}) => {
  const formattedInitialAnexos = anexos.reduce(
    (previousValue, currentValue) => {
      return { ...previousValue, [currentValue.name]: currentValue };
    },
    {}
  );

  const initial = {
    nomeBeneficiario,
    cpf,
    nascimento,
    nomeMaeBeneficiario,
    nomePaiBeneficiario,
    acao,
    ...formattedInitialAnexos,
  };

  return (
    <>
      <hr className="mt-32 mt-xs-0 mb-32" />
      <CollapseWithLabel
        label={
          <>
            <strong>Beneficiario: </strong>
            {nomeBeneficiario}
          </>
        }
      >
        <FormikBody type="update" initial={initial} setDisabled={setDisabled} />
      </CollapseWithLabel>
    </>
  );
};

const FormikBody = ({ initial, type, setDisabled }) => {
  const dispatch = useDispatch();
  const isUpdate = type === 'update';
  const [isEditing, setIsEditing] = useState(!isUpdate);
  const [calledReset, setCalledReset] = useState(false);

  useEffect(() => {
    setDisabled(isEditing);
  }, [isEditing, setDisabled]);

  return (
    <>
      <hr className="mt-32 mt-xs-0 mb-32" />
      <Formik
        validateOnMount
        initialValues={initial || BeneficiarioTaesaInitialValues}
        validationSchema={BeneficiarioTaesaSchema}
        onSubmit={(values, { resetForm }) => {
          setDisabled(false);

          const successMessage = 'Beneficiário adicionado com sucesso';

          if (isUpdate) {
            const nascimento =
              values?.nascimento?.toLocaleDateString('pt') || values.nascimento;

            dispatch(
              atualizaItemRascunhoRequest({
                campos: {
                  ...values,
                  nomePaiBeneficiario: values.nomePaiBeneficiario
                    ? values.nomePaiBeneficiario
                    : 'Não informado',
                  nascimento,
                },
                camposAntigos: initial,
                successMessage: successMessage.replace(
                  'adicionado',
                  'atualizado'
                ),
              })
            );

            setIsEditing(false);
          } else {
            dispatch(
              adicionaItemRascunhoRequest({
                campos: {
                  ...values,
                },
                successMessage,
              })
            );

            resetForm();
            setCalledReset(true);
            setTimeout(() => {
              setCalledReset(false);
            }, 150);
          }
        }}
      >
        {({ handleSubmit, isValid, values, resetForm, setFieldValue }) => (
          <Form>
            <div className="row mb-4">
              <div className="col-md-12 col-lg-6">
                <p className="mb-4">Ação</p>
                <Field
                  className="me-3"
                  name="acao"
                  color="orange"
                  type="radio"
                  label="Incluir"
                  component={Radio}
                  value="Incluir"
                  disabled={!isEditing}
                />
                <Field
                  className="me-3"
                  type="radio"
                  color="orange"
                  name="acao"
                  label="Alterar"
                  component={Radio}
                  value="Alterar"
                  disabled={!isEditing}
                />
                <Field
                  type="radio"
                  color="orange"
                  name="acao"
                  label="Excluir"
                  component={Radio}
                  value="Excluir"
                  disabled={!isEditing}
                  onChange={(e) => {
                    resetForm();
                    setFieldValue('acao', e.target.value, false);
                  }}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-6">
                <Field
                  className="mb-3"
                  name="nomeBeneficiario"
                  component={TextInput}
                  label="Nome completo do (a) Beneficiário"
                  disabled={!isEditing}
                />
              </div>

              {values.acao && values.acao !== 'Excluir' && (
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="cpf"
                    mask="111.111.111-11"
                    inputMode="numeric"
                    component={InputMaskField}
                    label="CPF Beneficiário"
                    disabled={!isEditing}
                  />
                </div>
              )}
            </div>
            {values.acao && values.acao !== 'Excluir' && (
              <div className="row">
                <div className="col-md-12 col-lg-6 mb-lg-32">
                  <Field
                    name="nascimento"
                    component={Datepicker}
                    datePickerClassName="formSize"
                    max={new Date()}
                    className="mb-3"
                    label="Data de Nascimento do Beneficiário"
                    disabled={!isEditing}
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                  />
                </div>
              </div>
            )}

            {values.acao && values.acao !== 'Excluir' && (
              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="nomeMaeBeneficiario"
                    component={TextInput}
                    label="Nome completo da Mãe do Beneficiário"
                    disabled={!isEditing}
                  />
                </div>
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="nomePaiBeneficiario"
                    component={TextInput}
                    label="Nome completo do Pai do Beneficiário"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            )}

            {values.acao && values.acao !== 'Excluir' && (
              <div className="row">
                <div className="col-md-12 ">
                  <p className="mb-4">
                    Com base na Lei nº 13.709/2018 (Lei Geral de Proteção de
                    Dados Pessoais), autorizo o tratamento dos meus dados
                    pessoais e dados pessoais sensíveis para fins relacionados
                    ao meu vínculo com a FORLUZ.
                  </p>
                  <Field
                    className="mb-3"
                    name="consentimentoDadosSensiveis"
                    component={Toggler}
                    label="Consentimento dados sensíveis (obrigatório)"
                  />
                </div>
              </div>
            )}

            {values.acao && values.acao !== 'Excluir' && (
              <Anexos
                disabled={!isEditing}
                reset={calledReset}
                cpf={values.cpf}
              />
            )}

            {isEditing ? (
              <>
                <hr className="mt-32 mt-xs-0 mb-32" />
                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        disabled={!isValid}
                        className="ms-0 ms-md-3"
                        onClick={handleSubmit}
                      >
                        {isUpdate ? 'Atualizar' : 'Adicionar'}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <hr className="mt-32 mt-xs-0 mb-32" />
                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        className="ms-0 ms-md-3"
                        onClick={() => {
                          dispatch(excluiItemRascunhoRequest(values));
                        }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        className="ms-0 ms-md-3"
                        onClick={() => setIsEditing(true)}
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export const BeneficiarioTaesa = ({ title, reference }) => {
  const [disabled, setDisabled] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const beneficiarios = useSelector((state) => state.user.formularioLista);

  useEffect(() => {
    dispatch(criaFormRascunhoRequest(reference));
  }, [dispatch, reference]);

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="fw-bold ts-20 mb-32">{title}</h3>
        <FormikBody setDisabled={setDisabled} />
        {beneficiarios.map((beneficiario) => {
          return (
            <CardBeneficiario
              key={beneficiario.nomeBeneficiario}
              {...beneficiario}
              setDisabled={setDisabled}
            />
          );
        })}
        {beneficiarios.length > 0 && (
          <>
            <hr className="mt-32 mt-xs-0 mb-32" />
            <div className="row">
              <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                <div className="d-block d-md-flex align-items-center">
                  <Button
                    disabled={disabled}
                    className="ms-0 ms-md-3"
                    onClick={() => {
                      dispatch(concluirFormRequest(reference));
                      navigate('/requerimentos/novo');
                    }}
                  >
                    Enviar
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
